// GenieX Villa Experience — the SVG villa canvas: rooms, devices, gate, main door.
import { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Layers } from "lucide-react";
import { ROOM_BOX, roomLabel, WATER_TANK_BOX, MAIN_DOOR_POS, GATE_POS, DRIVEWAY_BOX, VACUUM_DOCK, VACUUM_WAYPOINTS, VACUUM_CORRIDOR } from "./config";

/* ────────────────────────── Ambient BG ────────────────────────── */

export function AmbientBackground({ time, scene, SCENES }) {
  const palettes = {
    morning: ["#231d15", "#3a2b1a", "#5a3d1e", "#0f0d0a"],
    day:     ["#0e1218", "#1a2331", "#1f2a3a", "#0a0d13"],
    evening: ["#1a0f1d", "#2a1030", "#3d1a3e", "#0d0710"],
    night:   ["#04060c", "#0a0e1c", "#0e1428", "#02030a"],
  };
  const p = palettes[time] || palettes.night;
  const sceneAccent = scene && SCENES?.[scene]?.accent;

  return (
    <div className="fixed inset-0 z-0 pointer-events-none">
      <motion.div
        className="absolute inset-0"
        animate={{
          background: `radial-gradient(120% 90% at 30% 20%, ${p[2]} 0%, ${p[1]} 45%, ${p[0]} 100%)`,
        }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      />
      <AnimatePresence>
        {sceneAccent && (
          <motion.div
            key={scene}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.25 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
            style={{
              background: `radial-gradient(60% 40% at 50% 40%, ${sceneAccent} 0%, transparent 70%)`,
              mixBlendMode: "screen",
            }}
          />
        )}
      </AnimatePresence>
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }} />
    </div>
  );
}

/* ────────────────────────── Villa Canvas ──────────────────────── */

export function VillaCanvas({ state, activeRoom, setActiveRoom, onDoorbell }) {
  return (
    <div className="relative rounded-[24px] border border-gold/15 bg-black/40 backdrop-blur-md p-4 lg:p-6 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.7)]" data-testid="villa-canvas">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-mute">
          <Layers className="w-3.5 h-3.5 text-gold" />
          Villa · Ground Floor
        </div>
        <div className="text-[11px] text-dim tracking-wide">
          {roomLabel[activeRoom]} <span className="text-gold mx-1.5">·</span>
          {state.rooms[activeRoom]?.light > 0 ? `${state.rooms[activeRoom].light}% lit` : "dark"}
        </div>
      </div>

      <svg
        viewBox="0 0 900 640"
        className="w-full h-auto select-none"
        style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.4))" }}
      >
        <defs>
          <filter id="warmGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="14" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <radialGradient id="lightGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffe4a3" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#ffb347" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="floor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#161821" stopOpacity="1" />
            <stop offset="100%" stopColor="#0b0c14" stopOpacity="1" />
          </linearGradient>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1a1c26" strokeWidth="0.5" />
          </pattern>
          {/* Per-room clip paths — every room-scoped visual effect (light glow, RGB
              party wash, curtain fabric) is clipped to its own box so it can never
              bleed into a neighbouring room. */}
          {Object.entries(ROOM_BOX).map(([id, box]) => (
            <clipPath key={id} id={`clip-${id}`}>
              <rect x={box.x} y={box.y} width={box.w} height={box.h} rx="8" />
            </clipPath>
          ))}
        </defs>

        <rect x="20" y="40" width="860" height="580" rx="16" fill="url(#floor)" stroke="#242637" strokeWidth="1.2" />
        <rect x="20" y="40" width="860" height="580" rx="16" fill="url(#grid)" opacity="0.5" />

        {/* Driveway — the Main Gate sits at its outer edge, a walkway connects
            it to the Main Door directly above. */}
        <rect x={DRIVEWAY_BOX.x} y={DRIVEWAY_BOX.y} width={DRIVEWAY_BOX.w} height={DRIVEWAY_BOX.h} rx="10" fill="#0e0f18" stroke="#1e2030" strokeWidth="0.8" />
        <text x={DRIVEWAY_BOX.x + DRIVEWAY_BOX.w / 2} y={DRIVEWAY_BOX.y + 20} fontSize="9" letterSpacing="4" textAnchor="middle" fill="#3d3f52" fontFamily="ui-monospace, monospace">DRIVEWAY · GARDEN</text>
        {Array.from({ length: 8 }).map((_, i) => (
          <circle key={i} cx={360 + i * 60} cy={DRIVEWAY_BOX.y + 45} r={1.6} fill="#a8b872" opacity="0.55" />
        ))}
        {/* Walkway — Main Door -> garden -> driveway */}
        <line x1={MAIN_DOOR_POS.x} y1={MAIN_DOOR_POS.y} x2={MAIN_DOOR_POS.x} y2={DRIVEWAY_BOX.y} stroke="#2a2c3b" strokeWidth="10" strokeDasharray="3 5" opacity="0.7" />

        <PathwayLights on={state.rooms.kitchen.light > 0 || state.scene === "party" || state.scene === "good-morning"} />

        {Object.entries(ROOM_BOX).map(([id, box]) => (
          <Room
            key={id}
            id={id}
            box={box}
            room={state.rooms[id]}
            active={activeRoom === id}
            party={state.scene === "party" && id === "living"}
            onClick={() => setActiveRoom(id)}
          />
        ))}

        <WaterTank box={WATER_TANK_BOX} tank={state.waterTank} />

        {/* Devices — layered ON TOP of rooms */}
        <Sofa x={110} y={190} />
        <TV x={362} y={128} w={12} h={96} on={state.rooms.living.tv} vertical />
        <TV x={512} y={392} w={64} h={10} on={state.rooms.bedroom1.tv} />
        <Fan cx={230} cy={100} speed={state.rooms.living.fan} />
        <Fan cx={700} cy={260} speed={state.rooms.bedroom1.fan} />
        <Fan cx={240} cy={470} speed={state.rooms.bedroom2.fan} />
        <Fan cx={ROOM_BOX.dining.x + ROOM_BOX.dining.w - 30} cy={ROOM_BOX.dining.y + 26} speed={state.rooms.dining.fan} />
        <ACUnit box={ROOM_BOX.living} on={state.rooms.living.ac} />
        <ACUnit box={ROOM_BOX.bedroom1} on={state.rooms.bedroom1.ac} />
        <ACUnit box={ROOM_BOX.bedroom2}  on={state.rooms.bedroom2.ac} />
        <Curtains box={ROOM_BOX.living} pct={state.rooms.living.curtains} side="left" />
        <Curtains box={ROOM_BOX.bedroom1} pct={state.rooms.bedroom1.curtains} side="right" />
        <Curtains box={ROOM_BOX.bedroom2}  pct={state.rooms.bedroom2.curtains}  side="left" />
        <Bed x={500} y={226} w={90} h={128} />
        <Bed x={110} y={336} w={64} h={88} />
        <KitchenAppliance box={ROOM_BOX.kitchen} on={state.rooms.kitchen.appliance} />
        <DiningTable box={ROOM_BOX.dining} />
        <Bathtub box={ROOM_BOX.bath1} on={state.rooms.bath1.geyser} />
        <Bathtub box={ROOM_BOX.bath2} on={state.rooms.bath2.geyser} />

        <RobotVacuum vacuum={state.vacuum} />

        {/* Main vehicle gate + car, at the driveway's outer entrance */}
        <Gate open={state.gate === "open"} />

        {/* Safety hotspots */}
        <SensorDot x={ROOM_BOX.kitchen.x + 20} y={ROOM_BOX.kitchen.y + 20} label="GAS" />
        <SensorDot x={ROOM_BOX.bath1.x + 20}   y={ROOM_BOX.bath1.y + 20}   label="H2O" />
        <SensorDot x={ROOM_BOX.living.x + 20}  y={ROOM_BOX.living.y + 20}  label="AIR" />

        <AnimatePresence>
          {state.alarm === "gas" && <PulseRing key="gas" x={ROOM_BOX.kitchen.x + 100} y={ROOM_BOX.kitchen.y + 70} color="#f97316" />}
          {state.alarm === "fire" && <PulseRing key="fire" x={ROOM_BOX.kitchen.x + 100} y={ROOM_BOX.kitchen.y + 70} color="#ef4444" />}
          {state.alarm === "water" && <PulseRing key="water" x={ROOM_BOX.bath1.x + 100} y={ROOM_BOX.bath1.y + 40} color="#38bdf8" />}
        </AnimatePresence>

        {/* Main door — smart lock with face recognition, at the exact south wall
            of the Living Room, connected to the driveway by the walkway above. */}
        <MainDoor x={MAIN_DOOR_POS.x} y={MAIN_DOOR_POS.y} lockState={state.lock} onDoorbell={onDoorbell} />
      </svg>
    </div>
  );
}

/* ── Villa parts ─────────────────────────────────────────────── */

function Room({ id, box, room, active, party, onClick }) {
  const lit = (room?.light ?? 0) > 0;
  const litIntensity = (room?.light ?? 0) / 100;
  return (
    <g onClick={onClick} style={{ cursor: "pointer" }} data-testid={`villa-room-${id}`}>
      <motion.rect
        x={box.x} y={box.y} width={box.w} height={box.h} rx="8"
        fill="#14161f"
        stroke={active ? "#c9a961" : "#2a2c3b"}
        strokeWidth={active ? 1.6 : 1}
        animate={{ fill: lit ? `rgba(255,203,120,${0.06 + litIntensity * 0.12})` : "#14161f" }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
      {lit && (
        <motion.circle
          cx={box.x + box.w / 2}
          cy={box.y + box.h / 2}
          r={Math.max(box.w, box.h) * 0.55}
          fill="url(#lightGrad)"
          clipPath={`url(#clip-${id})`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.35 + litIntensity * 0.5 }}
          transition={{ duration: 0.8 }}
          style={{ mixBlendMode: "screen", pointerEvents: "none" }}
        />
      )}
      {party && <PartyGlow box={box} />}
      <text x={box.x + 12} y={box.y + 18} fontSize="9" letterSpacing="3" fill={active ? "#e2c07a" : "#5b5f74"} fontFamily="ui-monospace, monospace">
        {box.name.toUpperCase()}
      </text>
      <motion.circle
        cx={box.x + box.w / 2} cy={box.y + box.h / 2} r={2.5}
        animate={{ fill: lit ? "#ffdd8a" : "#3a3d4c", scale: lit ? [1, 1.15, 1] : 1 }}
        transition={{ duration: lit ? 2 : 0.4, repeat: lit ? Infinity : 0, ease: "easeInOut" }}
      />
    </g>
  );
}

// Party Mode's "main zone" — a slow, elegant RGB colour wash, strictly clipped
// to the Living Room box so it never touches a neighbouring room.
function PartyGlow({ box }) {
  return (
    <motion.rect
      x={box.x} y={box.y} width={box.w} height={box.h}
      clipPath={`url(#clip-living)`}
      style={{ mixBlendMode: "screen", pointerEvents: "none" }}
      animate={{ fill: ["#22d3ee33", "#818cf833", "#e879f933", "#22d3ee33"] }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
    />
  );
}

function Sofa({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x={0} y={0} width={150} height={44} rx={10} fill="#1c1f2b" stroke="#332a1c" strokeWidth="1" />
      {[0, 50, 100].map((dx) => (
        <rect key={dx} x={dx + 4} y={4} width={42} height={36} rx={7} fill="#262a3c" />
      ))}
      <rect x={-6} y={-2} width={8} height={48} rx={3} fill="#1c1f2b" stroke="#332a1c" strokeWidth="0.8" />
      <rect x={148} y={-2} width={8} height={48} rx={3} fill="#1c1f2b" stroke="#332a1c" strokeWidth="0.8" />
      <rect x={190} y={10} width={26} height={22} rx={4} fill="#20222e" stroke="#332a1c" strokeWidth="0.6" />
    </g>
  );
}

function TV({ x, y, w, h, on, vertical }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="1.5" fill="#22242f" stroke="#3a3d50" strokeWidth="0.5" />
      <motion.rect
        x={x + 1} y={y + 1} width={w - 2} height={h - 2}
        animate={{ fill: on ? "#7dd3fc" : "#0d0f16", opacity: on ? [0.7, 1, 0.85] : 0 }}
        transition={{ duration: 3, repeat: on ? Infinity : 0, ease: "easeInOut" }}
      />
      {vertical ? (
        <rect x={x - 4} y={y + h / 2 - 3} width={4} height={6} fill="#3a3d50" />
      ) : (
        <rect x={x + w / 2 - 3} y={y + h} width={6} height={4} fill="#3a3d50" />
      )}
    </g>
  );
}

function Fan({ cx, cy, speed = 0 }) {
  const dur = speed > 0 ? Math.max(0.35, 2.2 - speed * 0.55) : 0;
  return (
    <g>
      <circle cx={cx} cy={cy} r={13} fill="#181a24" stroke="#2c2f40" strokeWidth="0.7" />
      <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: speed > 0 ? `gx-fan-spin ${dur}s linear infinite` : "none" }}>
        {[0, 60, 120, 180, 240, 300].map((deg) => (
          <rect key={deg} x={cx - 1} y={cy - 11} width={2} height={10} rx={1} fill={speed > 0 ? "#8a8f9f" : "#43465a"} transform={`rotate(${deg} ${cx} ${cy})`} />
        ))}
      </g>
      <circle cx={cx} cy={cy} r={2} fill="#c9a961" />
    </g>
  );
}

function ACUnit({ box, on }) {
  const x = box.x + box.w - 60;
  const y = box.y + 8;
  return (
    <g>
      <rect x={x} y={y} width="50" height="10" rx="2" fill="#1a1c26" stroke="#2c2f40" strokeWidth="0.5" />
      <motion.rect x={x + 8} y={y + 3} width="34" height="1.5" rx={0.5} animate={{ fill: on ? "#7ec8ff" : "#2f3243" }} />
      <AnimatePresence>
        {on && Array.from({ length: 5 }).map((_, i) => (
          <motion.circle
            key={i}
            cx={x + 12 + i * 6}
            cy={y + 12}
            r={1.5}
            fill="#7ec8ff"
            initial={{ opacity: 0, cy: y + 12 }}
            animate={{ opacity: [0, 0.75, 0], cy: y + 44 }}
            transition={{ duration: 2.2, delay: i * 0.35, repeat: Infinity, ease: "easeOut" }}
          />
        ))}
      </AnimatePresence>
    </g>
  );
}

// Curtains gather toward each end of the track as `pct` (0-100, 0=closed,
// 100=open) rises — real curtains bunch, they don't need an empty track to
// slide into. Clipped to the room box so they can never spill into a
// neighbouring room even mid-animation.
function Curtains({ box, pct = 0, side }) {
  const geo =
    side === "right"  ? { x: box.x + box.w - 14, y: box.y + 30, w: 12, h: box.h - 60, dir: "v" } :
    side === "left"   ? { x: box.x + 2,          y: box.y + 30, w: 12, h: box.h - 60, dir: "v" } :
                        { x: box.x + 30, y: box.y + box.h - 14, w: box.w - 60, h: 12, dir: "h" };
  const isV = geo.dir === "v";
  const halfLen = isV ? geo.h / 2 : geo.w / 2;
  const bunchRatio = Math.max(6, halfLen * 0.24) / halfLen;
  const openness = Math.min(100, Math.max(0, pct)) / 100;
  const scale = 1 - openness * (1 - bunchRatio);

  const half1 = isV
    ? { x: geo.x, y: geo.y, width: geo.w, height: halfLen }
    : { x: geo.x, y: geo.y, width: halfLen, height: geo.h };
  const half2 = isV
    ? { x: geo.x, y: geo.y + halfLen, width: geo.w, height: halfLen }
    : { x: geo.x + halfLen, y: geo.y, width: halfLen, height: geo.h };
  const scaleKey = isV ? "scaleY" : "scaleX";

  const roomKey = Object.keys(ROOM_BOX).find((k) => ROOM_BOX[k] === box) || "";

  return (
    <g clipPath={`url(#clip-${roomKey})`} data-testid={`villa-curtains-${roomKey}`} data-openness={Math.round(openness * 100)}>
      {/* Window glass — glows warmer the more the curtains are drawn open */}
      <rect x={geo.x} y={geo.y} width={geo.w} height={geo.h} rx={2} fill="#0d3a52" opacity={0.16 + openness * 0.44} />

      <motion.rect
        {...half1}
        rx={2}
        fill="#4b3f68"
        style={{ transformOrigin: isV ? "top" : "left", transformBox: "fill-box" }}
        animate={{ [scaleKey]: scale }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.rect
        {...half2}
        rx={2}
        fill="#4b3f68"
        style={{ transformOrigin: isV ? "bottom" : "right", transformBox: "fill-box" }}
        animate={{ [scaleKey]: scale }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      />
    </g>
  );
}

// Headboard always against the wall at local y=0 (caller positions x/y so that
// wall-edge sits flush); feet point toward local +y, where a TV is placed to
// face them.
function Bed({ x, y, w, h }) {
  const pillowW = w * 0.32;
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x={-3} y={-6} width={w + 6} height={7} rx={2.5} fill="#2a2440" stroke="#3d3560" strokeWidth="0.6" />
      <rect x={0} y={0} width={w} height={h} rx="6" fill="#1b1e2a" stroke="#2f3244" strokeWidth="0.6" />
      <rect x={4} y={h * 0.32} width={w - 8} height={h * 0.62} rx="5" fill="#2c3050" />
      <path d={`M 4 ${h * 0.34} Q ${w / 2} ${h * 0.44} ${w - 4} ${h * 0.34}`} fill="none" stroke="#3a3e66" strokeWidth="0.7" opacity="0.7" />
      <rect x={6} y={5} width={pillowW} height={h * 0.24} rx="3" fill="#3a3d5a" />
      <rect x={w - pillowW - 6} y={5} width={pillowW} height={h * 0.24} rx="3" fill="#3a3d5a" />
    </g>
  );
}

function DiningTable({ box }) {
  const w = 76, h = 46;
  const x = box.x + box.w / 2 - w / 2;
  const y = box.y + box.h / 2 - h / 2 + 6;
  const chairXs = [x + w * 0.22, x + w * 0.5, x + w * 0.78];
  return (
    <g>
      {chairXs.map((cx, i) => (
        <rect key={`t${i}`} x={cx - 5} y={y - 15} width="10" height="10" rx="2.5" fill="#20222e" stroke="#2f3244" strokeWidth="0.5" />
      ))}
      {chairXs.map((cx, i) => (
        <rect key={`b${i}`} x={cx - 5} y={y + h + 5} width="10" height="10" rx="2.5" fill="#20222e" stroke="#2f3244" strokeWidth="0.5" />
      ))}
      <rect x={x} y={y} width={w} height={h} rx="8" fill="#1c1f2b" stroke="#332a1c" strokeWidth="1" />
      <rect x={x + 4} y={y + 4} width={w - 8} height={h - 8} rx="6" fill="#26293a" />
      {chairXs.map((cx, i) => (
        <ellipse key={`pm-t${i}`} cx={cx} cy={y + h * 0.28} rx="6" ry="4" fill="#312a1a" opacity="0.8" />
      ))}
      {chairXs.map((cx, i) => (
        <ellipse key={`pm-b${i}`} cx={cx} cy={y + h * 0.74} rx="6" ry="4" fill="#312a1a" opacity="0.8" />
      ))}
    </g>
  );
}

function KitchenAppliance({ box, on }) {
  const x = box.x + box.w - 60;
  const y = box.y + box.h - 40;
  return (
    <g>
      <rect x={x} y={y} width="46" height="28" rx="3" fill="#181a24" stroke="#2c2f40" strokeWidth="0.6" />
      <motion.circle cx={x + 12} cy={y + 14} r={4} animate={{ fill: on ? "#f97316" : "#3a3d4c", opacity: on ? [0.7, 1, 0.7] : 1 }} transition={{ duration: 1.4, repeat: on ? Infinity : 0 }} />
      <motion.circle cx={x + 34} cy={y + 14} r={4} animate={{ fill: on ? "#fb923c" : "#3a3d4c" }} />
    </g>
  );
}

function Bathtub({ box, on }) {
  const x = box.x + 14;
  const y = box.y + box.h - 34;
  return (
    <g>
      <rect x={x} y={y} width="60" height="22" rx="6" fill="#1a1c26" stroke="#2c2f40" strokeWidth="0.6" />
      <motion.rect x={x + 4} y={y + 4} width="52" height="14" rx="4" animate={{ fill: on ? "rgba(126,200,255,0.35)" : "#0f1119" }} />
    </g>
  );
}

// Smart Water Management — replaces the old Garage slot. Level rises while
// the motor is on, and (in auto mode) the motor is stopped by the caller once
// full — this component is purely a display of `tank` state.
function WaterTank({ box, tank }) {
  const tankX = box.x + 24, tankY = box.y + 24, tankW = 56, tankH = 56;
  const fillH = (Math.min(100, Math.max(0, tank.level)) / 100) * (tankH - 6);
  return (
    <g data-testid="villa-water-tank">
      <rect x={box.x} y={box.y} width={box.w} height={box.h} rx="8" fill="#12141d" stroke="#2a2c3b" strokeWidth="1" />
      <text x={box.x + 12} y={box.y + 14} fontSize="9" letterSpacing="3" fill="#5b5f74" fontFamily="ui-monospace, monospace">WATER TANK</text>
      <rect x={tankX} y={tankY} width={tankW} height={tankH} rx="6" fill="#0c0e16" stroke="#2c2f40" strokeWidth="1" />
      <motion.rect
        x={tankX + 3}
        height={fillH}
        y={tankY + tankH - 3 - fillH}
        width={tankW - 6}
        rx="4"
        fill="#3ea8e0"
        opacity="0.75"
        initial={{ height: fillH, y: tankY + tankH - 3 - fillH }}
        animate={{ height: fillH, y: tankY + tankH - 3 - fillH }}
        transition={{ duration: 1, ease: "easeInOut" }}
      />
      <text x={tankX + tankW / 2} y={tankY + tankH / 2 + 3} fontSize="10" textAnchor="middle" fill="#e6ecf5" fontFamily="ui-monospace, monospace">{Math.round(tank.level)}%</text>
      <g transform={`translate(${tankX + tankW + 22} ${tankY + 20})`}>
        <circle r={10} fill={tank.motorOn ? "rgba(126,200,255,0.15)" : "transparent"} stroke={tank.motorOn ? "#7ec8ff" : "#3a3d4c"} strokeWidth="1.2" />
        <motion.g style={{ transformOrigin: "0px 0px" }} animate={tank.motorOn ? { rotate: 360 } : { rotate: 0 }} transition={{ duration: 1.6, repeat: tank.motorOn ? Infinity : 0, ease: "linear" }}>
          {[0, 90, 180, 270].map((deg) => (
            <rect key={deg} x={-1} y={-7} width={2} height={5} fill={tank.motorOn ? "#7ec8ff" : "#4a4d5c"} transform={`rotate(${deg})`} />
          ))}
        </motion.g>
        <text x={0} y={22} fontSize="7" textAnchor="middle" fill="#5b5f74" fontFamily="ui-monospace, monospace">MOTOR</text>
      </g>
    </g>
  );
}

// Single physical robot vacuum — travels between waypoints through corridor
// stop(s) (see VACUUM_CORRIDOR) using a plain group transform (translate),
// never SVG x/y attributes, so it always looks like it's using a doorway
// rather than gliding straight through a wall.
function RobotVacuum({ vacuum }) {
  const active = vacuum.status === "cleaning";
  const toKey = active && vacuum.room ? vacuum.room : "dock";
  const prevKeyRef = useRef("dock");
  const fromKey = prevKeyRef.current;
  useEffect(() => {
    prevKeyRef.current = toKey;
  }, [toKey]);

  const endpoint = toKey === "dock" ? VACUUM_DOCK : VACUUM_WAYPOINTS[toKey];
  const corridor = VACUUM_CORRIDOR[`${fromKey}->${toKey}`] || [];
  const xs = [...corridor.map((p) => p.x), endpoint.x];
  const ys = [...corridor.map((p) => p.y), endpoint.y];

  return (
    <motion.g
      data-testid="villa-robot-vacuum"
      animate={{ x: xs, y: ys }}
      transition={{ duration: 1.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <motion.g
        animate={active ? { x: [0, 5, -4, 2, 0], y: [0, -3, 2, -2, 0] } : { x: 0, y: 0 }}
        transition={{ duration: 3.2, repeat: active ? Infinity : 0, ease: "easeInOut" }}
      >
        <circle r={7} fill="#181a24" stroke={active ? "#c9a961" : "#2c2f40"} strokeWidth="1" />
        {active && (
          <motion.circle
            r={7} fill="none" stroke="#c9a961" strokeWidth="0.8"
            initial={{ scale: 1, opacity: 0.7 }}
            animate={{ scale: 1.9, opacity: 0 }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <circle r={1.6} fill={active ? "#8ef58a" : "#3a3d4c"} />
      </motion.g>
    </motion.g>
  );
}

function Gate({ open }) {
  const cx = GATE_POS.x, cy = GATE_POS.y;
  return (
    <g transform={`translate(${cx} ${cy})`} data-testid="villa-gate" data-gate-open={open ? "true" : "false"}>
      <line x1={-60} y1={0} x2={-30} y2={0} stroke="#2f3244" strokeWidth="1.5" />
      <line x1={60}  y1={0} x2={30}  y2={0} stroke="#2f3244" strokeWidth="1.5" />
      <motion.g data-testid="villa-gate-door-left" style={{ originX: "-30px", originY: "0px" }} animate={{ rotate: open ? -70 : 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}>
        <rect x={-30} y={-2} width={28} height={4} rx={1.5} fill="#c9a961" />
      </motion.g>
      <motion.g data-testid="villa-gate-door-right" style={{ originX: "30px", originY: "0px" }} animate={{ rotate: open ? 70 : 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}>
        <rect x={2} y={-2} width={28} height={4} rx={1.5} fill="#c9a961" />
      </motion.g>
      <text x={0} y={16} textAnchor="middle" fontSize="8" letterSpacing="3" fill="#c9a961" fontFamily="ui-monospace, monospace">MAIN GATE</text>
      <motion.g data-testid="villa-gate-car" initial={false} animate={{ x: open ? 0 : -140, opacity: open ? 1 : 0 }} transition={{ duration: 1.8, delay: open ? 0.5 : 0, ease: [0.16, 1, 0.3, 1] }}>
        <rect x={-14} y={-24} width={28} height={12} rx={3} fill="#e2c07a" />
        <rect x={-11} y={-22} width={22} height={5} rx={2} fill="#151723" />
        <circle cx={-9} cy={-11} r={2.2} fill="#0b0c14" />
        <circle cx={9}  cy={-11} r={2.2} fill="#0b0c14" />
      </motion.g>
    </g>
  );
}

// The house's main entrance — a face-recognition smart lock, on the Living
// Room's south (exterior) wall, distinct from the vehicle "MAIN GATE" below.
function MainDoor({ x, y, lockState, onDoorbell }) {
  const locked = lockState !== "unlocked";
  const color = locked ? "#c9a961" : "#7ef58a";
  return (
    <g transform={`translate(${x} ${y})`} style={{ cursor: "pointer" }} onClick={onDoorbell} data-testid="villa-main-door">
      <rect x={-18} y={-32} width={62} height={42} fill="transparent" />
      <rect x={-9} y={-32} width={18} height={34} rx={2} fill="#14161f" stroke={color} strokeWidth="1.1" />
      <circle cx={4} cy={-14} r={1.3} fill={color} />
      <circle cx={0} cy={-25} r={2.3} fill="#0b0c14" stroke={color} strokeWidth="0.8" />
      <motion.circle
        cx={0} cy={-25} r={2.3}
        fill="none" stroke={color} strokeWidth="0.6"
        animate={{ scale: [1, 2.4], opacity: [0.65, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
      />
      <g transform="translate(14 -24)">
        <Bell x={0} y={0} width={11} height={11} color="#c9a961" />
      </g>
      <text x={0} y={12} textAnchor="middle" fontSize="7.5" letterSpacing="2" fill={color} fontFamily="ui-monospace, monospace">MAIN DOOR</text>
    </g>
  );
}

function PathwayLights({ on }) {
  return (
    <g>
      {[380, 440, 500, 560, 620, 680, 740, 800].map((x) => (
        <motion.circle key={x} cx={x} cy={DRIVEWAY_BOX.y + 25} r={2} animate={{ fill: on ? "#ffd68a" : "#3a3d4c", filter: on ? "drop-shadow(0 0 4px #ffd68a)" : "none" }} />
      ))}
    </g>
  );
}

function SensorDot({ x, y, label }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={3} fill="#0f1018" stroke="#3a3d50" strokeWidth="0.5" />
      <motion.circle r={5} fill="none" stroke="#4b5163" strokeWidth="0.6" animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
      <text x={7} y={2} fontSize="6" fill="#5b5f74" fontFamily="ui-monospace, monospace">{label}</text>
    </g>
  );
}

function PulseRing({ x, y, color }) {
  return (
    <motion.g>
      {[0, 0.4, 0.8].map((delay) => (
        <motion.circle key={delay} cx={x} cy={y} r={20} fill="none" stroke={color} strokeWidth="2" initial={{ opacity: 0.9, scale: 0.4 }} animate={{ opacity: 0, scale: 3 }} transition={{ duration: 1.6, delay, repeat: Infinity }} />
      ))}
    </motion.g>
  );
}
